import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SocialLogin } from '@capgo/capacitor-social-login';

// ponytail: drive.file limits to files this app created; spreadsheets.readonly for Sheet import
const MANIFEST_NAME = 'punchlog-manifest.json';
const FOLDER_NAME = 'PunchLog';
const SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/spreadsheets.readonly',
].join(' ');

const lastSyncAt = ref(null);
const syncError  = ref(null);
const isSyncing  = ref(false);

// ── Token persistence: Capacitor Preferences (native) or localStorage (web) ──
const TOKEN_KEY   = 'gdrive_token';
const EXPIRY_KEY  = 'gdrive_token_exp';
const REFRESH_KEY = 'gdrive_refresh_token';

const store = {
  get:    k    => Capacitor.isNativePlatform()
    ? Preferences.get({ key: k }).then(r => r.value)
    : Promise.resolve(localStorage.getItem(k)),
  set:    (k, v) => Capacitor.isNativePlatform()
    ? Preferences.set({ key: k, value: v })
    : (localStorage.setItem(k, v), Promise.resolve()),
  remove: k    => Capacitor.isNativePlatform()
    ? Preferences.remove({ key: k })
    : (localStorage.removeItem(k), Promise.resolve()),
};

// In-memory cache — seeded from localStorage on web, restored async on native
let _cachedToken    = Capacitor.isNativePlatform() ? null : (localStorage.getItem(TOKEN_KEY) ?? null);
let _tokenExpiresAt = Capacitor.isNativePlatform() ? 0    : parseInt(localStorage.getItem(EXPIRY_KEY) ?? '0', 10);
let _refreshToken   = Capacitor.isNativePlatform() ? null : (localStorage.getItem(REFRESH_KEY) ?? null);

if (Capacitor.isNativePlatform()) {
  Promise.all([store.get(TOKEN_KEY), store.get(EXPIRY_KEY), store.get(REFRESH_KEY)]).then(([t, e, r]) => {
    if (t) { _cachedToken = t; _tokenExpiresAt = parseInt(e ?? '0', 10); }
    if (r) _refreshToken = r;
  });
}

function setCachedToken(token, expiresInSeconds = 3599, refreshToken = null) {
  _cachedToken    = token;
  _tokenExpiresAt = Date.now() + (expiresInSeconds - 60) * 1000;
  store.set(TOKEN_KEY,  token);
  store.set(EXPIRY_KEY, String(_tokenExpiresAt));
  if (refreshToken) {
    _refreshToken = refreshToken;
    store.set(REFRESH_KEY, refreshToken);
  }
}

export function clearCachedToken() {
  _cachedToken    = null;
  _tokenExpiresAt = 0;
  _refreshToken   = null;
  store.remove(TOKEN_KEY);
  store.remove(EXPIRY_KEY);
  store.remove(REFRESH_KEY);
}

// ── PKCE helpers (Web Crypto API — available in all modern browsers + Node 18+) ─
async function generatePKCE() {
  const rand = crypto.getRandomValues(new Uint8Array(48));
  const verifier = btoa(String.fromCharCode(...rand))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return { verifier, challenge };
}

const PROXY_URL = import.meta.env.VITE_OAUTH_PROXY_URL;

async function exchangeCode(code, verifier, redirectUri) {
  if (!PROXY_URL) throw new Error('VITE_OAUTH_PROXY_URL not configured');
  const res = await fetch(`${PROXY_URL}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, code_verifier: verifier, redirect_uri: redirectUri }),
  });
  const data = await res.json();
  if (!res.ok || !data.access_token) throw new Error(data.error ?? 'token_exchange_failed');
  return data; // { access_token, refresh_token, expires_in }
}

async function silentRefresh() {
  if (!_refreshToken || !PROXY_URL) throw new Error('token_expired');
  const res = await fetch(`${PROXY_URL}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: _refreshToken }),
  });
  const data = await res.json();
  if (!res.ok || !data.access_token) throw new Error('token_expired');
  setCachedToken(data.access_token, data.expires_in ?? 3599);
  return data.access_token;
}

// ── Web OAuth — PKCE authorization code flow ──────────────────────────────────
function webOAuth() {
  return new Promise(async (resolve, reject) => {
    const redirectUri = window.location.origin + '/oauth/callback';
    try {
      const { verifier, challenge } = await generatePKCE();
      const params = new URLSearchParams({
        client_id:             import.meta.env.VITE_GOOGLE_CLIENT_ID,
        redirect_uri:          redirectUri,
        response_type:         'code',
        scope:                 SCOPES,
        code_challenge:        challenge,
        code_challenge_method: 'S256',
        access_type:           'offline',
        prompt:                'consent', // ensures refresh_token is returned
      });

      const popup = window.open(
        `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
        'google-oauth',
        'width=520,height=620,left=200,top=100',
      );
      if (!popup) { reject(new Error('Popup blocked')); return; }

      function onMessage(e) {
        if (e.origin !== window.location.origin || e.data?.type !== 'google-oauth-pkce') return;
        cleanup();
        if (e.data.code) {
          exchangeCode(e.data.code, verifier, redirectUri)
            .then(d => resolve({ token: d.access_token, expiresIn: d.expires_in ?? 3599, refreshToken: d.refresh_token ?? null }))
            .catch(reject);
        } else {
          reject(new Error(e.data.error || 'OAuth failed'));
        }
      }

      const poll = setInterval(() => {
        if (popup.closed) { cleanup(); reject(new Error('Popup closed')); }
      }, 500);

      function cleanup() { clearInterval(poll); window.removeEventListener('message', onMessage); }
      window.addEventListener('message', onMessage);
    } catch (err) {
      reject(err);
    }
  });
}

// ── Native OAuth (Capacitor plugin) ──────────────────────────────────────────
let _nativeInitialized = false;
async function nativeOAuth() {
  if (!_nativeInitialized) {
    await SocialLogin.initialize({
      google: {
        webClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        mode: 'online',
      },
    });
    _nativeInitialized = true;
  }
  const res = await SocialLogin.login({ provider: 'google', options: { scopes: [SCOPES] } });
  if (res?.result?.accessToken?.token) return { token: res.result.accessToken.token, expiresIn: res.result.accessToken.expiresIn ?? 3599 };
  throw new Error('Native OAuth failed');
}

// Proactively refresh 5 min before expiry. On web (PKCE), silentRefresh()
// exchanges the stored refresh_token without user interaction.
// On native, the plugin re-auths silently at the OS level on next driveSignIn().
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

async function getAccessToken() {
  if (_cachedToken && Date.now() < _tokenExpiresAt - REFRESH_BUFFER_MS) return _cachedToken;
  if (_refreshToken && !Capacitor.isNativePlatform()) return silentRefresh();
  throw new Error('token_expired');
}

// ── Drive API ─────────────────────────────────────────────────────────────────
async function driveRequest(token, path, opts = {}) {
  const res = await fetch(`https://www.googleapis.com${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) throw new Error(`Drive ${opts.method ?? 'GET'} ${path} → ${res.status}`);
  return res.json();
}

async function ensureFolder(token) {
  const q = `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
  const { files } = await driveRequest(token, `/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`);
  if (files.length) return files[0].id;
  const folder = await driveRequest(token, '/drive/v3/files', {
    method: 'POST',
    body: JSON.stringify({ name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
  });
  return folder.id;
}

async function uploadDriveFile(token, folderId, name, content) {
  return uploadDriveBlob(token, folderId, name, new Blob([JSON.stringify(content)], { type: 'application/json' }), 'application/json');
}

async function uploadDriveBlob(token, folderId, name, blob, mimeType) {
  const q = `name='${name}' and '${folderId}' in parents and trashed=false`;
  const { files } = await driveRequest(token, `/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`);
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify({ name, ...(files.length ? {} : { parents: [folderId] }), mimeType })], { type: 'application/json' }));
  form.append('file', blob);
  const url = files.length
    ? `https://www.googleapis.com/upload/drive/v3/files/${files[0].id}?uploadType=multipart`
    : 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
  const res = await fetch(url, { method: files.length ? 'PATCH' : 'POST', headers: { Authorization: `Bearer ${token}` }, body: form });
  if (!res.ok) throw new Error(`uploadDriveBlob ${name} → ${res.status}`);
}

async function downloadDriveFile(token, folderId, name) {
  const q = `name='${name}' and '${folderId}' in parents and trashed=false`;
  const { files } = await driveRequest(token, `/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)`);
  if (!files.length) return null;
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${files[0].id}?alt=media`, { headers: { Authorization: `Bearer ${token}` } });
  return res.ok ? res.json() : null;
}

// ── Public API ────────────────────────────────────────────────────────────────
const USER_CANCELLED = new Set(['Popup closed', 'Popup blocked']);
export const isUserCancelledOAuth = e => USER_CANCELLED.has(e?.message);

export function resolveManifest(local, remote) {
  if (!local) return remote;
  if (!remote) return local;
  return new Date(local.updatedAt) >= new Date(remote.updatedAt) ? local : remote;
}

export async function driveSignIn() {
  const result = await (Capacitor.isNativePlatform() ? nativeOAuth() : webOAuth());
  setCachedToken(result.token, result.expiresIn, result.refreshToken ?? null);
}

export async function driveSignOut() {
  clearCachedToken();
  if (Capacitor.isNativePlatform() && _nativeInitialized) {
    await SocialLogin.logout({ provider: 'google' }).catch(() => {});
  }
}

export function useGoogleDriveSync() {
  async function uploadData(name, data) {
    const token = await getAccessToken();
    const folderId = await ensureFolder(token);
    return uploadDriveFile(token, folderId, name, data);
  }

  async function uploadBlob(name, blob, mimeType) {
    const token = await getAccessToken();
    const folderId = await ensureFolder(token);
    return uploadDriveBlob(token, folderId, name, blob, mimeType);
  }

  async function downloadData(name) {
    const token = await getAccessToken();
    const folderId = await ensureFolder(token);
    return downloadDriveFile(token, folderId, name);
  }

  async function fetchSheetValues(sheetId) {
    const token = await getAccessToken();
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(sheetId)}/values/A1:Z`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Sheets API ${sheetId} → ${res.status}`);
    const { values } = await res.json();
    return values ?? [];
  }

  return { signIn: driveSignIn, signOut: driveSignOut, uploadData, uploadBlob, downloadData, fetchSheetValues, lastSyncAt, syncError, isSyncing };
}
